// Small layout pieces every Office screen uses: a page header, the refused /
// failed states, and the confirm sheet. Never a blank screen — a refusal is a
// sentence a bookkeeper can act on.

import React, { useEffect, useState } from 'react';
import { EmptyState, Notice, Skeleton, FwButton, Sheet, usePhoneLayout } from '../../../ui/fw';
import { describeError, serverSentence } from '../../shared/http';
import { usePageChrome } from '../../shell/chrome';

/**
 * The header's actions, flattened to a list this component can count.
 *
 * Every call site writes them as a fragment of conditionals — Invoices nests a
 * fragment INSIDE a fragment, InvoiceDetail is eight `cond ? <FwButton/> :
 * null` in a row — so `React.Children.toArray(actions)` returns ONE child (the
 * fragment) and a naive `.length` says every screen has a single action.
 * `React.Children.forEach` already drops `null`, `undefined` and booleans; the
 * only thing it will not do is look inside a fragment, so that is all this
 * adds. Recursive, because the nesting is two deep today and nothing stops it
 * being three.
 *
 * Exported because `office/records/OfficePage.js` — the OTHER PageHeader, the
 * one serving records/insight/admin — collapses its actions the same way and
 * has to count them the same way. Only the COUNTING is shared: that header
 * takes `children` rather than `actions` and paints `or-` classes, so its JSX
 * is its own. See the note on the two headers at PageHeader below.
 */
export function actionList(node) {
  const out = [];
  const walk = (n) => {
    React.Children.forEach(n, (child) => {
      if (child === null || child === undefined || typeof child === 'boolean') return;
      if (React.isValidElement(child) && child.type === React.Fragment) { walk(child.props.children); return; }
      out.push(child);
    });
  };
  walk(node);
  return out;
}

/**
 * Which of them is the one verb the screen exists for.
 *
 * A screen can say outright, with `primaryAction`: an index, or the
 * `data-action` of the button it means. A name that matches nothing falls back
 * rather than hiding the primary — the one thing this component must never do.
 *
 * Otherwise it is answered from what the screen already wrote, in two steps:
 *
 *   1. the first action explicitly marked `variant="primary"`;
 *   2. failing that, the first action.
 *
 * ── Step 1 was added after step 1 alone got it WRONG, in a browser ─────────
 *
 * "The first action" was the whole rule to begin with, on the reasoning that
 * Office screens write their actions most-important-first — Invoices leads
 * with "Bill finished jobs" and says so in a comment, InvoiceDetail leads with
 * Send. Then scripts/ui-check.js failed `invoice-from-jobs` at 390px:
 *
 *     <PageHeader title="Invoice from jobs" …>
 *       <FwButton variant="ghost" …>Cancel</FwButton>
 *       <FwButton variant="primary" …>Create invoice</FwButton>
 *
 * — so "the first" was CANCEL, and the one verb the screen exists for went
 * into the sheet. The scenario timed out looking for it, which is the same
 * thing a bookkeeper would have experienced. A screen that marks its main
 * action `variant="primary"` has already answered this question, and reading
 * that is strictly better than assuming an ordering.
 *
 * An FwButton that omits `variant` RENDERS as primary (Button.js defaults it)
 * and is deliberately not matched here: the fallback picks the first action
 * anyway, so the only case it could change is a screen mixing an implicit
 * primary with an explicit one — where guessing at the implicit one would be
 * exactly the assumption this step exists to stop making.
 *
 * Exported for the same reason as actionList: both headers answer this
 * question, and answering it twice is how they start disagreeing.
 */
export function primaryIndexOf(items, want) {
  if (typeof want === 'number' && Number.isInteger(want) && want >= 0 && want < items.length) return want;
  if (typeof want === 'string' && want) {
    const i = items.findIndex((el) => React.isValidElement(el) && el.props && el.props['data-action'] === want);
    if (i >= 0) return i;
  }
  const marked = items.findIndex((el) => React.isValidElement(el) && el.props && el.props.variant === 'primary');
  return marked >= 0 ? marked : 0;
}

/**
 * A screen's header — and, on a phone, the SHELL's.
 *
 * This repo has two PageHeaders: `office/records/OfficePage.js` serves the
 * records, insight and admin screens, and this one serves `office/screens/`.
 * Only the first was ever taught that the one shell already prints the
 * screen's name in the phone header, so these four still said their own name
 * twice — measured in a browser at 390px on 2026-09-10:
 *
 *   /overview  ["Overview","Overview"]   /invoices   ["Invoices","Invoices"]
 *   /estimates ["Estimates","Estimates"] /payroll    ["Payroll","Payroll"]
 *
 * ── The rule is about the TITLE, not about the width ───────────────────────
 *
 * A plain string is something the shell can show, so it is handed up and this
 * header stops repeating it on a phone. A RICH title is not: InvoiceDetail
 * passes the number plus a status Pill, and measured the same morning
 * /invoices/1 read shell "Invoices" over page "INV-2026-0001 Draft" — the
 * header naming the section while the page names the record, which is not a
 * duplicate and must not be "fixed" by deleting the invoice number from the
 * screen. So a non-string title keeps its heading at every width.
 *
 * The meta and the actions always stay. A due date and a Send button are the
 * page's, not the shell's, and dropping the whole header would take them.
 *
 * OfficeSharedPageHeader.test.js holds all four directions.
 *
 * ── And on a phone the ACTIONS are one row, not a column of them ───────────
 *
 * Measured in Chromium at 320×568 on 2026-09-11, against the built app:
 *
 *   /invoices     header 196px tall, three buttons stacked one per line at
 *                 y=122 / 178 / 234, the first invoice card at y=525 — a LIST
 *                 screen with no record on the first screenful.
 *   /invoices/1   header 394px of a 568px viewport, six buttons at
 *                 y=152 … 432, the first card of the invoice at y=606.
 *
 * Shortening the labels cannot fix it, and that was measured too: "Bill
 * finished jobs" renders 243px and "New invoice" 188px inside a 288px content
 * column, so those two cannot pair at ANY label length. The row is not too
 * wordy; there are simply more actions than a phone has width, and a header
 * that spends the whole first screen is the "browser stuffed into an app"
 * docs/APP_REBUILD.md was written about.
 *
 * So on a phone one action stays a button and the rest move into a sheet,
 * behind an overflow control that sits up on the TITLES row:
 *
 *     [ 12 of 12 ............................... (⋮) ]
 *     [ BILL FINISHED JOBS                          ]
 *
 * Nothing is removed and nothing changes what it does — the same elements are
 * rendered, in a different place. On a desktop `actions` is rendered exactly
 * as it was passed, untouched, because there the row fits.
 *
 * ── Why the overflow control is not simply the second button in the row ────
 *
 * That was the first shape, and it was measured rather than assumed. To keep
 * scripts/ui-check.js honest the primary has to stay under 60% of the viewport
 * whenever a control sits beside it — 192px at 320px — and "Bill finished
 * jobs" is a 243px button. Capped to fit, its label broke to one word per line
 * and the button grew to 72px tall: the header came down only 196px → 163px,
 * and the main verb of the screen became unreadable. The titles row, by
 * contrast, is one 18px line of meta on a phone (the shell prints the screen's
 * name), so the control costs nothing there and the primary gets the whole
 * width, which is the only width its label fits in.
 *
 * Three things this deliberately does NOT do:
 *   - it does not collapse a lone action. One button is not a row.
 *   - it does not hide the primary. A screen's main verb is a button at every
 *     width; that is the whole contract.
 *   - it does not require a single call site to change. `primaryAction` exists
 *     for a screen that wants to name a different one, and defaults to the
 *     first, which is already the important one everywhere (primaryIndexOf).
 *
 * ── One thing a browser scenario now has to do, and it is not a defect ─────
 *
 * A scenario that drives a header action ON A PHONE has to open the overflow
 * first, because the action is no longer in the header row — it is in the
 * sheet. `scripts/ui-check.js` has exactly one of those, `reverse-payment`,
 * which clicks `[data-action="mark-unpaid"]` straight off the page and times
 * out at 390px. It needs these two lines before the locator, and they are a
 * no-op on a desktop because the control does not exist there:
 *
 *     const more = page.locator('[data-action="page-more"]');
 *     if (await more.count() && await more.isVisible()) { await more.click(); await page.waitForTimeout(300); }
 *
 * Verified 2026-09-11: with them the scenario is 47/47 at both widths, which
 * is also the proof that the sheet opens and its buttons are pressable in a
 * real browser rather than only in jsdom.
 */
export function PageHeader({ title, meta, actions, primaryAction, children }) {
  const phone = usePhoneLayout();
  const [moreOpen, setMoreOpen] = useState(false);
  // Only a string can go up: a React element would arrive at the shell as
  // "[object Object]", and `''` leaves AppShell on its documented fallback to
  // the destination's label.
  const plain = typeof title === 'string' ? title : '';
  usePageChrome(plain);

  const items = actionList(actions);
  const collapse = phone && items.length > 1;
  const primaryIdx = collapse ? primaryIndexOf(items, primaryAction) : 0;
  const rest = collapse ? items.filter((_, i) => i !== primaryIdx) : [];

  // Rotating to a desktop width (or a tablet turning landscape) takes the
  // sheet away with it. Without this the sheet is still "open" on the way
  // back, over a row that is no longer collapsed.
  useEffect(() => { if (!collapse) setMoreOpen(false); }, [collapse]);

  // Pressing an action in the sheet closes the sheet. Several of these open a
  // ConfirmSheet of their own (Delete, Send, Reverse payment), and two sheets
  // at once is a backdrop over a backdrop. The close runs FIRST so the
  // original handler is the last thing to happen and nothing can swallow it.
  const inSheet = (el, i) => {
    if (!React.isValidElement(el)) return <span key={`more-${i}`}>{el}</span>;
    const orig = el.props && el.props.onClick;
    return React.cloneElement(el, {
      key: el.key || `more-${i}`,
      onClick: (e) => { setMoreOpen(false); if (typeof orig === 'function') orig(e); },
    });
  };

  // The primary says, in the DOM, that it means to span its row. ui-check's
  // "no control eats the screen" rule exempts `.fw-btn--full` by class and its
  // own comment says why: an exemption should be something a reviewer can see,
  // not a number that happens to sit above a line. The class is merged rather
  // than set, so a screen that passed its own className keeps it.
  const primaryEl = collapse && React.isValidElement(items[primaryIdx])
    ? React.cloneElement(items[primaryIdx], {
      className: [items[primaryIdx].props.className, 'fw-btn--full'].filter(Boolean).join(' '),
    })
    : items[primaryIdx];

  const titles = (
    <div className="of-page__titles">
      {phone && plain ? null : <h1 className="of-page__title fw-display">{title}</h1>}
      {meta ? <div className="of-page__meta">{meta}</div> : null}
    </div>
  );

  return (
    <header className={collapse ? 'of-page__head of-page__head--collapsed' : 'of-page__head'}>
      {collapse ? (
        // One LINE, not two flex items that may or may not share one. As
        // direct children of `.of-page__head` (which wraps) these broke apart
        // whenever the heading's content was wide: measured at 320px on
        // /invoices/1, the h1 "INV-2026-0001 Draft" has a 280px max-content
        // width, so 280 + 60 exceeded the 288px row and the control took a
        // third line of its own — 174px of header. Inside a `nowrap` line the
        // heading shrinks instead (it already carries `min-width: 0`).
        <div className="of-page__topline">
          {titles}
          <FwButton
            variant="ghost"
            icon="more"
            className="of-page__more-btn"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={moreOpen ? 'true' : 'false'}
            aria-label={`${rest.length} more action${rest.length === 1 ? '' : 's'}`}
            title={`${rest.length} more action${rest.length === 1 ? '' : 's'}`}
            data-action="page-more"
            data-more-count={rest.length}
          />
        </div>
      ) : titles}
      {actions ? (
        <div className={collapse ? 'of-page__actions of-page__actions--collapsed' : 'of-page__actions'}>
          {collapse ? primaryEl : actions}
        </div>
      ) : null}
      {collapse ? (
        <Sheet
          open={moreOpen}
          title="More actions"
          onClose={() => setMoreOpen(false)}
          className="of-page__more"
          data-page-more-sheet
        >
          <div className="of-page__more-list">{rest.map(inSheet)}</div>
        </Sheet>
      ) : null}
      {children}
    </header>
  );
}

/** The screen is not for this person. */
export function Refused({ what = 'this screen', reason }) {
  return (
    <EmptyState icon="lock" title={`You do not have access to ${what}`}>
      {reason || 'Ask an admin to grant the permission if you need it.'}
    </EmptyState>
  );
}

/** A request failed — one sentence, and a retry when there is one. */
export function LoadFailed({ error, onRetry, what = 'this' }) {
  return (
    <EmptyState icon="alertTriangle" title={`Could not load ${what}`} action={onRetry ? <FwButton variant="ghost" onClick={onRetry}>Try again</FwButton> : null}>
      {describeError(error)}
    </EmptyState>
  );
}

export function Loading({ lines = 4 }) {
  return <Skeleton lines={lines} className="of-loading" />;
}

/**
 * A picker's OPTIONS could not be loaded, but the screen around it still works.
 *
 * `LoadFailed` replaces the screen; this sits above a control that is still
 * usable. The distinction matters because an empty `<select>` is a *claim* —
 * "there are none" — and a refused request makes it a false one that looks like
 * data rather than like an error, so nobody questions it. That is what the
 * classic Estimates screen did: the client roster answered 403, the dropdown
 * offered only "— New client —", the name was typed in by hand and the estimate
 * saved with no `client_id`, i.e. a duplicate customer created by a request
 * that never answered.
 *
 * `consequence` is required rather than optional on purpose: "could not load X"
 * alone does not tell a bookkeeper whether the empty control below is safe to
 * type into, which is the only decision they have to make.
 */
export function PickerFailed({ error, onRetry, what, consequence }) {
  return (
    <Notice tone="warn" data-picker-failed={what}>
      Could not load {what} — {describeError(error)}. {consequence}
      {onRetry ? <> <FwButton variant="quiet" onClick={onRetry}>Try again</FwButton></> : null}
    </Notice>
  );
}

/**
 * An inline error line under an action; `null` renders nothing.
 *
 * `serverSentence`, not `describeError`. The difference only shows on a 401 or
 * a 403 and it is the whole point: describeError flattens EVERY 403 to "You do
 * not have permission to do that.", so a server that took the trouble to say
 * "Only admin can delete timesheets" or "This week is already approved; reject
 * it before changing hours" had that thrown away and replaced with a sentence
 * that does not say what to do next. serverSentence prefers what the server
 * actually said and falls back to the same generic line when it said nothing,
 * so nothing is lost and the specific cases are gained.
 *
 * This was silently degrading every refusal in Office — found by the agent
 * building the money screens, which is where the specific 403s live.
 */
export function ActionError({ error, onDismiss }) {
  if (!error) return null;
  return <Notice tone="stop" onDismiss={onDismiss}>{serverSentence(error)}</Notice>;
}

/**
 * Yes/no sheet.
 *
 * ── `confirmDisabled`, and why it defaults to today's behaviour ─────────────
 *
 * Added 2026-09-13. A confirm sheet can already KNOW that the thing it is
 * about to do cannot be done: measured in Chromium at 320px, InvoiceDetail's
 * send sheet printed "This invoice has no email address to go to" and offered
 * a SEND whose `disabled` read `false`. The server refuses that send correctly
 * (400 `no_recipient`), so it was a dead control rather than a false success —
 * a request spent to be told, under the button, what was already written above
 * it.
 *
 * Eight screens share this component and most of their sheets have nothing to
 * check, so the prop is opt-in and `false`: a call site that does not pass it
 * renders exactly the markup it rendered before. The one thing NOT to do with
 * it is disable a confirm on a guess — EstimateDetail's send sheet looks like
 * the same case and is not, because POST /estimates/:id/send marks the
 * estimate sent whether or not an email goes anywhere, so its warning is
 * honest and its button is live. A sheet may only disable its confirm when it
 * can state the server's own refusal (apps/office/shared/moneyEndpoints.js
 * sendRecipient, and the `known` flag on it).
 *
 * `busy` still wins on its own: FwButton disables itself while `loading`.
 */
export function ConfirmSheet({ open, title, onClose, onConfirm, confirmLabel = 'Confirm', tone = 'primary', busy = false, confirmDisabled = false, children }) {
  return (
    <Sheet
      open={open}
      title={title}
      onClose={onClose}
      footer={(
        <div className="of-sheet__footer">
          <FwButton variant="ghost" onClick={onClose} disabled={busy}>Cancel</FwButton>
          <FwButton
            variant={tone}
            onClick={onConfirm}
            loading={busy}
            disabled={confirmDisabled}
            data-confirm={confirmDisabled ? 'blocked' : 'ready'}
          >{confirmLabel}</FwButton>
        </div>
      )}
    >
      {children}
    </Sheet>
  );
}
